import { z } from 'zod';

/**
 * Wave 30 — Registro de Não Conformidade (RNC).
 *
 * Non-conformance register used during project execution. Whenever
 * something departs from spec / drawing / norma técnica, the PM logs an
 * RNC. A correcting party fixes it, a different party verifies the fix.
 *
 * Status lifecycle
 * ────────────────
 *   aberto     → RNC logged, correction not yet started
 *   em_analise → someone is investigating / planning the fix
 *   corrigido  → corrective action applied; awaits verification
 *   verificado → verification passed; RNC closed
 *
 * Severity scale is wider than the punch-list's 3-bucket priority: RNCs
 * carry compliance weight, and "critical" items may trigger stop-work.
 */

export const rncStatusSchema = z.enum([
  'aberto',
  'em_analise',
  'corrigido',
  'verificado',
]);
export type RncStatus = z.infer<typeof rncStatusSchema>;

export const rncSeveritySchema = z.enum([
  'baixa',
  'media',
  'alta',
  'critica',
]);
export type RncSeverity = z.infer<typeof rncSeveritySchema>;

export const rncItemSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  area: z.string().nullable(),
  severity: rncSeveritySchema,
  status: rncStatusSchema,
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  attachmentId: z.string().nullable(),
  attachmentFilename: z.string().nullable(),
  dueDate: z.string().nullable(),
  // Wave 379BJ · #92 — data do incidente em obra (separada de
  // created_at). Quando preenchida, RncPage usa pra cross-link com
  // o RDO daquele dia (em vez do proxy created_at). Nullable pra
  // RNCs antigas e pra casos onde PM não sabe a data.
  incidentDate: z.string().nullable().optional(),
  // W155 · 2026-05-30 — inspeção FVS que originou a RNC, quando o registro
  // foi criado a partir de uma FVS reprovada. Nullable: RNCs sem origem
  // FVS (registro manual) e RNCs antigas ficam null.
  fvsInspectionId: z.string().nullable().optional(),
  reportedBy: z.string(),
  reportedByName: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  verifiedBy: z.string().nullable(),
  verifiedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RncItem = z.infer<typeof rncItemSchema>;

export const createRncItemRequestSchema = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2000).optional(),
  area: z.string().trim().max(120).optional(),
  severity: rncSeveritySchema.default('media'),
  assigneeUserId: z.string().optional().nullable(),
  attachmentId: z.string().optional().nullable(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
    .optional()
    .nullable(),
  // Wave 379BJ · #92 · data do incidente. Optional na criação;
  // PM-validável depois.
  incidentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
    .optional()
    .nullable(),
  // W155 · 2026-05-30 — vínculo opcional com a inspeção FVS de origem.
  fvsInspectionId: z.string().optional().nullable(),
});
export type CreateRncItemRequest = z.infer<typeof createRncItemRequestSchema>;

export const updateRncItemRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(160).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    area: z.string().trim().max(120).nullable().optional(),
    severity: rncSeveritySchema.optional(),
    status: rncStatusSchema.optional(),
    assigneeUserId: z.string().nullable().optional(),
    attachmentId: z.string().nullable().optional(),
    dueDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
      .nullable()
      .optional(),
    // Wave 379BJ · #92 · data do incidente editável.
    incidentDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
      .nullable()
      .optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
  });
export type UpdateRncItemRequest = z.infer<typeof updateRncItemRequestSchema>;

/** Per-status and per-severity counts; drives the header + readiness chip. */
export const rncSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: z.object({
    aberto: z.number().int().nonnegative(),
    em_analise: z.number().int().nonnegative(),
    corrigido: z.number().int().nonnegative(),
    verificado: z.number().int().nonnegative(),
  }),
  bySeverity: z.object({
    baixa: z.number().int().nonnegative(),
    media: z.number().int().nonnegative(),
    alta: z.number().int().nonnegative(),
    critica: z.number().int().nonnegative(),
  }),
  /** How many RNCs are past due and still not verificado. */
  overdue: z.number().int().nonnegative(),
  /** Conformance rate: 0-100 = verificado / total. */
  conformancePercent: z.number(),
});
export type RncSummary = z.infer<typeof rncSummarySchema>;

export const listRncResponseSchema = z.object({
  items: z.array(rncItemSchema),
  summary: rncSummarySchema,
});
export type ListRncResponse = z.infer<typeof listRncResponseSchema>;
